<template>
  <div class="review-panel">
    <!-- Aktiviteter har två vyer: typmappning + lista -->
    <div v-if="isActivities" class="seg">
      <button :class="{ on: view === 'types' }" @click="view = 'types'">Typer <span class="seg-n">{{ ds.typeMappings.length }}</span></button>
      <button :class="{ on: view === 'items' }" @click="view = 'items'">Aktiviteter <span class="seg-n">{{ fmtInt(ds.items.length) }}</span></button>
    </div>

    <!-- ── Typmappning (Google-typ → aktivitetstyp) ── -->
    <template v-if="isActivities && view === 'types'">
      <p class="hint">Googles aktivitetstyper kopplas till ditt aktivitetsbibliotek. Förslag hämtas från bibliotekets Google Fit-mappning.</p>
      <div class="rows">
        <div v-for="t in ds.typeMappings" :key="t.googleType" class="trow" :class="{ todo: t.action === 'map' && !t.activityTypeId, ignore: t.action === 'ignore' }">
          <div class="trow-top">
            <span class="gtype"><span class="g">G</span> {{ prettyGoogleType(t.googleType) }}</span>
            <span class="tmeta">{{ fmtInt(t.count) }} st <span v-if="t.autoMatched" class="tag">auto</span></span>
          </div>
          <div class="trow-act">
            <div class="actions">
              <button :class="{ on: t.action === 'map' }" @click="t.action = 'map'; touch()">Koppla</button>
              <button :class="{ on: t.action === 'ignore' }" @click="t.action = 'ignore'; touch()">Ignorera</button>
            </div>
            <select v-if="t.action === 'map'" v-model="t.activityTypeId" class="sel" :class="{ missing: !t.activityTypeId }" @change="touch()">
              <option :value="null" disabled>Välj aktivitetstyp…</option>
              <optgroup v-for="cat in groupedTypes" :key="cat.key" :label="cat.label">
                <option v-for="a in cat.types" :key="a.id" :value="a.id">{{ a.isCardio ? '❤️ ' : '' }}{{ a.nameSv }}</option>
              </optgroup>
            </select>
            <span v-else class="muted">{{ fmtInt(t.count) }} aktiviteter hoppas över</span>
          </div>
        </div>
      </div>
    </template>

    <!-- ── Postlista ── -->
    <template v-else>
      <div class="toolbar">
        <div class="filters">
          <button v-for="f in filters" :key="f.key" class="fchip" :class="{ on: filter === f.key }" @click="filter = f.key">{{ f.label }} <span>{{ fmtInt(f.count) }}</span></button>
        </div>
        <div v-if="years.length > 1" class="filters years">
          <button class="fchip" :class="{ on: year === 'all' }" @click="year = 'all'">Alla år</button>
          <button v-for="y in years" :key="y.year" class="fchip" :class="{ on: year === y.year }" @click="year = y.year">{{ y.year }} <span>{{ fmtInt(y.count) }}</span></button>
        </div>
        <div class="bulk">
          <button class="bulk-btn" @click="bulk(filtered, 'new')">Bocka nya{{ scopeSuffix }}</button>
          <button v-if="counts.overlap" class="bulk-btn" @click="bulk(filtered, 'overlap', true)">Ta med överlappande</button>
          <button class="bulk-btn" @click="bulk(filtered, null)">Avbocka{{ scopeSuffix }}</button>
          <span class="range">{{ fmtInt(selectedInFilter) }} av {{ fmtInt(filtered.length) }} valda</span>
        </div>
      </div>

      <div class="rows">
        <template v-for="g in groups" :key="g.key">
          <div class="month">
            <span class="month-label">{{ g.label }}</span>
            <span class="month-meta">{{ fmtInt(g.all.length) }} st · {{ fmtInt(g.all.filter(i => i.selected).length) }} valda</span>
            <span class="month-actions">
              <button class="mini" title="Bocka alla nya i månaden" @click="bulk(g.all, 'new')">✓ nya</button>
              <button class="mini" title="Avbocka månaden" @click="bulk(g.all, null)">✕</button>
            </span>
          </div>
          <label v-for="it in g.items" :key="it.id" class="irow" :class="{ off: !it.selected, [it.status]: true }">
            <input type="checkbox" :checked="it.selected" :disabled="it.status === 'invalid'" @change="setSel(it, $event.target.checked)" />
            <div class="irow-main">
              <div class="irow-top">
                <span class="ititle">{{ title(it) }}</span>
                <span class="badge" :class="tone(it.status)">{{ label(it.status) }}</span>
              </div>
              <div class="irow-meta">{{ meta(it) }}<span v-if="it.statusReason" class="reason"> · {{ it.statusReason }}</span></div>
            </div>
          </label>
        </template>
        <p v-if="!filtered.length" class="empty">Inga poster matchar filtret.</p>
        <div v-if="remaining > 0" ref="sentinel" class="more">
          <button class="bulk-btn" @click="showMore">Visa fler ({{ fmtInt(remaining) }} kvar)</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { dataset, importState, touch } from '../../../services/import/importSession.js';
import { STATUS_LABELS, MEASUREMENT_KINDS, datasetCounts, fmtInt } from '../../../services/import/importModel.js';
import { useTimeline } from './useTimeline.js';

const CATEGORY_LABELS = {
  WALK: 'Promenad', HIKE: 'Vandring', RUN: 'Löpning', CYCLE: 'Cykling', SWIM: 'Simning', WATER: 'Vatten',
  CARDIO: 'Kondition', FITNESS: 'Rörlighet', STRENGTH: 'Styrka', MARTIAL_ARTS: 'Kampsport & fäktning',
  SPORT: 'Sport', WINTER: 'Vinter', OTHER: 'Övrigt',
};

export default {
  name: 'ImportReviewPanel',
  props: {
    datasetKey: { type: String, required: true },
    activityTypes: { type: Array, default: () => [] },
  },
  setup(props) {
    const ds = computed(() => dataset(props.datasetKey));
    const isActivities = computed(() => props.datasetKey === 'activities');
    const view = ref('items');
    const filter = ref('all');

    // Alla listor läser importState.rev så att ändringar på (icke-reaktiva) poster syns.
    const counts = computed(() => { void importState.rev; return datasetCounts(ds.value); });
    const filters = computed(() => {
      const c = counts.value;
      const list = [{ key: 'all', label: 'Alla', count: c.total }];
      for (const s of ['new', 'update', 'duplicate', 'overlap', 'invalid']) {
        if (c[s]) list.push({ key: s, label: STATUS_LABELS[s].label, count: c[s] });
      }
      return list;
    });
    const statusFiltered = computed(() => {
      void importState.rev;
      return filter.value === 'all' ? ds.value.items : ds.value.items.filter(it => it.status === filter.value);
    });
    const { year, years, filtered, groups, remaining, showMore, sentinel } = useTimeline(statusFiltered);
    const selectedInFilter = computed(() => { void importState.rev; return filtered.value.filter(i => i.selected).length; });
    const scopeSuffix = computed(() => (year.value !== 'all' ? ` ${year.value}` : filter.value !== 'all' ? ' i urvalet' : ' alla'));

    function setSel(it, on) { it.selected = on; touch(); }
    // status=null ⇒ avbocka; add=true ⇒ lägg till status utan att röra övriga.
    function bulk(list, status, add = false) {
      for (const it of list) {
        if (status === null) it.selected = false;
        else if (add) { if (it.status === status) it.selected = true; }
        else it.selected = it.status === status || it.status === 'update';
      }
      touch();
    }

    const groupedTypes = computed(() => {
      const map = new Map();
      for (const t of props.activityTypes) {
        if (!map.has(t.category)) map.set(t.category, { key: t.category, label: CATEGORY_LABELS[t.category] || t.category, types: [] });
        map.get(t.category).types.push(t);
      }
      return [...map.values()];
    });
    const typeName = id => props.activityTypes.find(t => t.id === id)?.nameSv;
    const prettyGoogleType = g => String(g).replace(/[._]/g, ' ').replace(/^\w/, c => c.toUpperCase());

    const fmtDT = iso => new Date(iso).toLocaleString('sv-SE', { dateStyle: 'medium', timeStyle: 'short' });
    const fmtD = iso => new Date(iso).toLocaleDateString('sv-SE', { dateStyle: 'medium' });
    const fmtDur = s => { const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60); return h ? `${h} h ${m} min` : `${m} min`; };

    function title(it) {
      switch (props.datasetKey) {
        case 'activities': {
          const tm = ds.value.typeMappings.find(t => t.googleType === it.googleType);
          const mapped = tm?.action === 'map' && tm.activityTypeId ? typeName(tm.activityTypeId) : null;
          return `${it.name || prettyGoogleType(it.googleType)}${mapped ? ` → ${mapped}` : ''}`;
        }
        case 'weights': return `${it.weightKg.toLocaleString('sv-SE')} kg${it.fatPercent != null ? ` · ${it.fatPercent} % fett` : ''}`;
        case 'measurements': return MEASUREMENT_KINDS.filter(k => it.values[k.key] != null).map(k => `${k.label} ${it.values[k.key]}`).join(' · ');
        case 'height': return `${it.heightCm} cm`;
        case 'steps': return `${fmtInt(it.steps)} steg`;
        default: return '';
      }
    }
    function meta(it) {
      switch (props.datasetKey) {
        case 'activities': {
          const parts = [fmtDT(it.startedAt), fmtDur(it.durationSeconds)];
          if (it.distanceMeters) parts.push(`${(it.distanceMeters / 1000).toFixed(1)} km`);
          if (it.kcal) parts.push(`${Math.round(it.kcal)} kcal`);
          if (it.avgHeartRate) parts.push(`♥ ${it.avgHeartRate}`);
          if (it.hasRoute) parts.push('🗺 rutt');
          return parts.join(' · ');
        }
        case 'steps': return fmtD(it.date);
        default: return fmtDT(it.date);
      }
    }
    const label = s => STATUS_LABELS[s]?.label ?? s;
    const tone = s => STATUS_LABELS[s]?.tone ?? 'grey';

    return {
      ds, isActivities, view, filter, counts, filters,
      year, years, filtered, groups, remaining, showMore, sentinel, selectedInFilter, scopeSuffix,
      setSel, bulk, touch, groupedTypes, prettyGoogleType, title, meta, label, tone, fmtInt,
    };
  },
};
</script>

<style scoped>
.review-panel { display: flex; flex-direction: column; gap: 0.8rem; }
.hint { margin: 0; font-size: 0.83rem; color: var(--gym-text-muted); line-height: 1.5; }
.muted { font-size: 0.8rem; color: var(--gym-text-muted); }

.seg { display: inline-flex; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-pill); padding: 3px; align-self: flex-start; }
.seg button { border: none; background: transparent; color: var(--gym-text-muted); padding: 0.4rem 0.95rem; border-radius: var(--gym-radius-pill); font-weight: 700; font-size: 0.86rem; cursor: pointer; display: inline-flex; gap: 0.4rem; align-items: center; }
.seg button.on { background: var(--gym-accent); color: var(--gym-on-accent); }
.seg-n { font-size: 0.75rem; opacity: 0.85; }

.toolbar { display: flex; flex-direction: column; gap: 0.5rem; }
.filters { display: flex; gap: 0.35rem; flex-wrap: wrap; }
.filters.years { padding-top: 0.15rem; border-top: 1px dashed var(--gym-border); }
.fchip { border: 1px solid var(--gym-border); background: var(--gym-surface); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.28rem 0.7rem; font-size: 0.78rem; font-weight: 700; cursor: pointer; display: inline-flex; gap: 0.35rem; }
.fchip span { opacity: 0.7; }
.fchip.on { background: var(--gym-accent-soft); color: var(--gym-accent); border-color: var(--gym-accent-border); }
.bulk { display: flex; gap: 0.4rem; flex-wrap: wrap; align-items: center; }
.bulk-btn { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text); border-radius: var(--gym-radius-sm); padding: 0.4rem 0.7rem; font-size: 0.8rem; font-weight: 600; cursor: pointer; }
.bulk-btn:hover { border-color: var(--gym-accent-border); color: var(--gym-accent); }
.range { margin-left: auto; font-size: 0.76rem; color: var(--gym-text-faint); white-space: nowrap; }

.rows { display: flex; flex-direction: column; gap: 0.45rem; }
.empty { color: var(--gym-text-muted); font-size: 0.88rem; text-align: center; padding: 1rem; margin: 0; }
.more { display: flex; justify-content: center; padding: 0.4rem 0; }

/* Månadsrubrik – klistrar sig under panelhuvudet */
.month { display: flex; align-items: center; gap: 0.6rem; position: sticky; top: -0.5rem; z-index: 2; background: var(--gym-surface); padding: 0.45rem 0.2rem 0.3rem; margin-top: 0.3rem; border-bottom: 1px solid var(--gym-border); }
.month:first-child { margin-top: 0; }
.month-label { font-weight: 800; color: var(--gym-text); font-size: 0.86rem; text-transform: capitalize; }
.month-meta { font-size: 0.76rem; color: var(--gym-text-muted); }
.month-actions { margin-left: auto; display: inline-flex; gap: 0.3rem; }
.mini { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.15rem 0.55rem; font-size: 0.72rem; font-weight: 700; cursor: pointer; }
.mini:hover { color: var(--gym-accent); border-color: var(--gym-accent-border); }

.irow { display: flex; gap: 0.7rem; align-items: flex-start; border: 1px solid var(--gym-border); border-left-width: 4px; border-radius: var(--gym-radius); padding: 0.55rem 0.8rem; background: var(--gym-surface-2); cursor: pointer; }
.irow.new { border-left-color: var(--gym-success); }
.irow.update { border-left-color: var(--gym-accent); }
.irow.duplicate { border-left-color: var(--gym-border-strong); }
.irow.overlap { border-left-color: var(--gym-warning); }
.irow.invalid { border-left-color: var(--gym-danger); }
.irow.off { opacity: 0.6; }
.irow input { margin-top: 0.2rem; accent-color: var(--gym-accent); width: 18px; height: 18px; flex-shrink: 0; }
.irow-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.15rem; }
.irow-top { display: flex; justify-content: space-between; gap: 0.5rem; align-items: center; }
.ititle { font-weight: 700; color: var(--gym-text); font-size: 0.9rem; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.irow-meta { font-size: 0.78rem; color: var(--gym-text-muted); }
.reason { color: var(--gym-text-faint); }

.badge { font-size: 0.7rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: var(--gym-radius-pill); white-space: nowrap; }
.badge.ok { background: var(--gym-success-soft); color: var(--gym-success); }
.badge.grey { background: var(--gym-surface-3); color: var(--gym-text-muted); }
.badge.warn { background: var(--gym-warning-soft); color: var(--gym-warning); }
.badge.info { background: var(--gym-accent-soft); color: var(--gym-accent); }
.badge.danger { background: var(--gym-danger-soft); color: var(--gym-danger); }

/* Typmappning */
.trow { border: 1px solid var(--gym-border); border-left-width: 4px; border-left-color: var(--gym-success); border-radius: var(--gym-radius); padding: 0.6rem 0.8rem; background: var(--gym-surface-2); display: flex; flex-direction: column; gap: 0.45rem; }
.trow.todo { border-left-color: var(--gym-danger); background: var(--gym-danger-soft); }
.trow.ignore { border-left-color: var(--gym-border-strong); opacity: 0.6; }
.trow-top { display: flex; justify-content: space-between; gap: 0.5rem; align-items: baseline; }
.gtype { font-weight: 700; color: var(--gym-text); font-size: 0.92rem; display: inline-flex; gap: 0.45rem; align-items: center; }
.g { display: inline-grid; place-items: center; width: 20px; height: 20px; border-radius: 50%; background: #fff; color: #4285f4; font-weight: 800; font-size: 0.8rem; font-family: Arial, sans-serif; }
.tmeta { font-size: 0.78rem; color: var(--gym-text-muted); display: inline-flex; gap: 0.35rem; align-items: center; }
.tag { font-size: 0.68rem; font-weight: 700; padding: 0.1rem 0.4rem; border-radius: var(--gym-radius-pill); background: var(--gym-accent-soft); color: var(--gym-accent); }
.trow-act { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; }
.actions { display: inline-flex; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); overflow: hidden; flex-shrink: 0; }
.actions button { border: none; background: var(--gym-surface); color: var(--gym-text-muted); padding: 0.38rem 0.7rem; font-size: 0.8rem; font-weight: 700; cursor: pointer; border-right: 1px solid var(--gym-border); }
.actions button:last-child { border-right: none; }
.actions button.on { background: var(--gym-accent); color: var(--gym-on-accent); }
.sel { flex: 1; min-width: 180px; padding: 0.4rem 0.6rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface); color: var(--gym-text); font-size: 0.85rem; }
.sel.missing { border-color: var(--gym-danger); }

@media (max-width: 600px) {
  .bulk { flex-wrap: nowrap; overflow-x: auto; padding-bottom: 0.2rem; scrollbar-width: none; }
  .bulk-btn { white-space: nowrap; flex-shrink: 0; }
  .filters.years { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; }
  .filters.years .fchip { flex-shrink: 0; }
  .range { display: none; }
  .sel { min-width: 0; width: 100%; }
  .month-meta { display: none; }
}
</style>
